// Vistas de Operación, del lado del administrador.
//
// El trabajador marca su asistencia y reporta desde su teléfono; esto es la
// otra cara: quién llegó, quién falta, qué se reportó y qué está pendiente
// de aprobar.

import { useCallback, useEffect, useState } from 'react';
import {
  AlertOctagon,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  Loader2,
  ListTodo,
  UserCheck,
} from 'lucide-react';

import { workforceApi, WorkforceApiError } from './api';
import { CATEGORIAS } from './types';
import type { Absence, Attendance, ChecklistRun, Incident, Task } from './types';
import { posApi } from '@/modules/pos';
import type { Location } from '@/modules/pos';

const hora = (iso: string) =>
  new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
const fecha = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('es-MX');
const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function Shell({
  icon: Icon,
  title,
  subtitle,
  action,
  children,
}: {
  icon: typeof Clock;
  title: string;
  subtitle: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground leading-tight">{title}</h1>
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

const Spinner = () => (
  <div className="flex items-center justify-center py-16 text-muted-foreground">
    <Loader2 className="w-6 h-6 animate-spin" />
  </div>
);

function ErrorBox({ error }: { error: string }) {
  return (
    <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-500">
      {error}
    </div>
  );
}

function Empty({ icon: Icon, title, hint }: { icon: typeof Clock; title: string; hint: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Icon className="w-10 h-10 text-muted-foreground mb-3" />
      <p className="text-foreground font-medium">{title}</p>
      <p className="text-sm text-muted-foreground mt-1 max-w-md">{hint}</p>
    </div>
  );
}

function useLocations() {
  const [locations, setLocations] = useState<Location[]>([]);
  useEffect(() => {
    posApi.locations().then(setLocations).catch(() => undefined);
  }, []);
  return locations;
}

// ── Asistencia ──────────────────────────────────────────────────

export function AttendanceView() {
  const locations = useLocations();
  const [dia, setDia] = useState(hoyISO());
  const [marcas, setMarcas] = useState<Attendance[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMarcas(null);
    workforceApi
      .attendance({ business_date: dia })
      .then(setMarcas)
      .catch((e: WorkforceApiError) => setError(e.message));
  }, [dia]);

  const nombre = (id: string) => locations.find((l) => l.id === id)?.name || '—';

  // Se agrupa por persona: lo que el administrador quiere saber es quién
  // llegó y a qué hora, no una lista cronológica de marcas sueltas.
  const porPersona = new Map<string, { entrada?: Attendance; salida?: Attendance }>();
  (marcas || []).forEach((m) => {
    const actual = porPersona.get(m.person) || {};
    if (m.kind === 'in') actual.entrada = m;
    else actual.salida = m;
    porPersona.set(m.person, actual);
  });

  return (
    <Shell
      icon={UserCheck}
      title="Asistencia"
      subtitle="Quién entró, a qué hora y desde dónde"
      action={
        <input
          type="date"
          value={dia}
          onChange={(e) => setDia(e.target.value)}
          className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
        />
      }
    >
      {error && <ErrorBox error={error} />}
      {!marcas && !error && <Spinner />}
      {marcas && porPersona.size === 0 && (
        <Empty
          icon={UserCheck}
          title="Nadie marcó este día"
          hint="Las marcas llegan desde el teléfono de cada quien, con foto y ubicación."
        />
      )}

      <div className="grid gap-2">
        {[...porPersona.entries()].map(([persona, m]) => {
          const retardo = m.entrada?.classification === 'late';
          return (
            <div key={persona} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-medium text-foreground">{persona}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {m.entrada ? nombre(m.entrada.location_id) : '—'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-foreground tabular-nums">
                    {m.entrada ? hora(m.entrada.happened_at) : '—'}
                    {' → '}
                    {m.salida ? hora(m.salida.happened_at) : '…'}
                  </p>
                  {retardo && (
                    <p className="text-xs text-amber-500">
                      retardo de {m.entrada!.minutes_late} min
                    </p>
                  )}
                  {m.entrada && !m.salida && (
                    <p className="text-xs text-muted-foreground">sin marcar salida</p>
                  )}
                </div>
              </div>
              {m.entrada?.photo_url && (
                <a
                  href={m.entrada.photo_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary underline mt-2 inline-block"
                >
                  Ver foto de entrada
                </a>
              )}
            </div>
          );
        })}
      </div>
    </Shell>
  );
}

// ── Ausencias ───────────────────────────────────────────────────

const KIND_LABEL: Record<string, string> = {
  absence: 'Falta',
  vacation: 'Vacaciones',
  holiday: 'Día festivo',
};

export function AbsencesView() {
  const [items, setItems] = useState<Absence[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const cargar = useCallback(() => {
    workforceApi
      .absences()
      .then(setItems)
      .catch((e: WorkforceApiError) => setError(e.message));
  }, []);

  useEffect(cargar, [cargar, tick]);

  const decidir = async (id: string, aprobar: boolean) => {
    setError(null);
    try {
      await workforceApi.decideAbsence(id, aprobar);
      setTick((t) => t + 1);
    } catch (e) {
      setError(e instanceof WorkforceApiError ? e.message : 'No se pudo decidir');
    }
  };

  const pendientes = (items || []).filter((a) => a.status === 'pending');
  const resueltas = (items || []).filter((a) => a.status !== 'pending');

  return (
    <Shell icon={CalendarDays} title="Ausencias" subtitle="Vacaciones, faltas y festivos">
      {error && <ErrorBox error={error} />}
      {!items && !error && <Spinner />}
      {items && !items.length && (
        <Empty icon={CalendarDays} title="Sin solicitudes" hint="Las vacaciones requieren tu aprobación antes de contar." />
      )}

      {pendientes.length > 0 && (
        <>
          <p className="text-sm font-medium text-foreground mb-2">Esperando tu decisión</p>
          <div className="grid gap-2 mb-6">
            {pendientes.map((a) => (
              <div key={a.id} className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="font-medium text-foreground">
                      {a.person} · {KIND_LABEL[a.kind] || a.kind}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {fecha(a.starts_on)} → {fecha(a.ends_on)}
                      {a.note && ` · ${a.note}`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => void decidir(a.id, true)}
                      className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground"
                    >
                      Aprobar
                    </button>
                    <button
                      type="button"
                      onClick={() => void decidir(a.id, false)}
                      className="rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground"
                    >
                      Rechazar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="grid gap-2">
        {resueltas.map((a) => (
          <div key={a.id} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between gap-3">
            <div>
              <p className="text-foreground">
                {a.person} · {KIND_LABEL[a.kind] || a.kind}
              </p>
              <p className="text-xs text-muted-foreground">
                {fecha(a.starts_on)} → {fecha(a.ends_on)}
              </p>
            </div>
            <span
              className={`text-xs px-2 py-1 rounded-full border ${
                a.status === 'approved'
                  ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500'
                  : 'border-border text-muted-foreground'
              }`}
            >
              {a.status === 'approved' ? 'Aprobada' : 'Rechazada'}
            </span>
          </div>
        ))}
      </div>
    </Shell>
  );
}

// ── Checklists ──────────────────────────────────────────────────

export function ChecklistsView() {
  const locations = useLocations();
  const [dia, setDia] = useState(hoyISO());
  const [runs, setRuns] = useState<ChecklistRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRuns(null);
    workforceApi
      .runs({ business_date: dia })
      .then(setRuns)
      .catch((e: WorkforceApiError) => setError(e.message));
  }, [dia]);

  const nombre = (id: string) => locations.find((l) => l.id === id)?.name || '—';

  return (
    <Shell
      icon={ClipboardCheck}
      title="Checklists"
      subtitle="Apertura y cierre, con su evidencia"
      action={
        <input
          type="date"
          value={dia}
          onChange={(e) => setDia(e.target.value)}
          className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
        />
      }
    >
      {error && <ErrorBox error={error} />}
      {!runs && !error && <Spinner />}
      {runs && !runs.length && (
        <Empty
          icon={ClipboardCheck}
          title="Sin checklists este día"
          hint="Se abren desde el teléfono al empezar o cerrar el turno."
        />
      )}

      <div className="grid gap-3">
        {runs?.map((r) => {
          const hechos = r.steps.filter((s) => s.done).length;
          return (
            <div key={r.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div>
                  <p className="font-medium text-foreground">
                    {r.template_name} · {nombre(r.location_id)}
                  </p>
                  <p className="text-xs text-muted-foreground">{r.person}</p>
                </div>
                {r.completed_at ? (
                  <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/30">
                    Completo · {hora(r.completed_at)}
                  </span>
                ) : (
                  <span className="text-xs px-2 py-1 rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/30">
                    {hechos} de {r.steps.length}
                  </span>
                )}
              </div>
              <div className="grid gap-1.5">
                {r.steps.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 text-sm">
                    <CheckCircle2
                      className={`w-4 h-4 shrink-0 ${s.done ? 'text-emerald-500' : 'text-muted-foreground/40'}`}
                    />
                    <span className={s.done ? 'text-foreground' : 'text-muted-foreground'}>
                      {s.text}
                    </span>
                    {s.requires_photo && (
                      <span className="text-xs text-muted-foreground">(con foto)</span>
                    )}
                    {s.photo_url && (
                      <a
                        href={s.photo_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-primary underline"
                      >
                        ver
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Shell>
  );
}

// ── Incidencias ─────────────────────────────────────────────────

const SEV_LABEL: Record<string, string> = { low: 'Baja', medium: 'Media', high: 'Grave' };

export function IncidentsView() {
  const locations = useLocations();
  const [items, setItems] = useState<Incident[] | null>(null);
  const [severidad, setSeveridad] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setItems(null);
    workforceApi
      .incidents(severidad ? { severity: severidad } : {})
      .then(setItems)
      .catch((e: WorkforceApiError) => setError(e.message));
  }, [severidad]);

  const nombre = (id: string) => locations.find((l) => l.id === id)?.name || '—';

  return (
    <Shell icon={AlertOctagon} title="Incidencias" subtitle="Lo que reporta el equipo">
      <div className="flex gap-2 mb-5">
        {[
          { v: '', l: 'Todas' },
          { v: 'high', l: 'Graves' },
          { v: 'medium', l: 'Medias' },
          { v: 'low', l: 'Bajas' },
        ].map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => setSeveridad(o.v)}
            className={`px-3 py-1.5 rounded-lg border text-sm ${
              o.v === severidad
                ? 'border-primary bg-primary/15 text-foreground font-medium'
                : 'border-border bg-card text-muted-foreground'
            }`}
          >
            {o.l}
          </button>
        ))}
      </div>

      {error && <ErrorBox error={error} />}
      {!items && !error && <Spinner />}
      {items && !items.length && (
        <Empty icon={AlertOctagon} title="Sin incidencias" hint="Buena señal." />
      )}

      <div className="grid gap-2">
        {items?.map((i) => {
          const grave = i.severity === 'high';
          return (
            <div
              key={i.id}
              className={`rounded-xl border p-4 ${
                grave ? 'border-red-500/40 bg-red-500/10' : 'border-border bg-card'
              }`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-medium text-foreground">{i.option_text}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {CATEGORIAS[i.category] || i.category} · {nombre(i.location_id)} ·{' '}
                    {i.reported_by} · {hora(i.happened_at)}
                  </p>
                  {i.note && <p className="text-sm text-muted-foreground mt-1">{i.note}</p>}
                </div>
                <span
                  className={`text-xs px-2 py-1 rounded-full border shrink-0 ${
                    grave
                      ? 'border-red-500/40 text-red-500'
                      : 'border-border text-muted-foreground'
                  }`}
                >
                  {SEV_LABEL[i.severity] || i.severity}
                </span>
              </div>
              {i.photo_url && (
                <a
                  href={i.photo_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary underline mt-2 inline-block"
                >
                  Ver foto
                </a>
              )}
            </div>
          );
        })}
      </div>
    </Shell>
  );
}

// ── Tareas ──────────────────────────────────────────────────────

export function TasksView() {
  const locations = useLocations();
  const [items, setItems] = useState<Task[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [texto, setTexto] = useState('');
  const [locationId, setLocationId] = useState('');
  const [asignado, setAsignado] = useState('');
  const [dia, setDia] = useState(hoyISO());

  useEffect(() => {
    workforceApi
      .tasks(false)
      .then(setItems)
      .catch((e: WorkforceApiError) => setError(e.message));
  }, [tick]);

  useEffect(() => {
    if (!locationId && locations.length) setLocationId(locations[0].id);
  }, [locations, locationId]);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await workforceApi.createTask({
        text: texto.trim(),
        location_id: locationId,
        due_on: dia,
        assigned_to: asignado.trim(),
      });
      setTexto('');
      setAsignado('');
      setTick((t) => t + 1);
    } catch (err) {
      setError(err instanceof WorkforceApiError ? err.message : 'No se pudo crear');
    }
  };

  const nombre = (id: string) => locations.find((l) => l.id === id)?.name || '—';

  return (
    <Shell icon={ListTodo} title="Tareas" subtitle="Montaje, desmontaje y pendientes">
      <form
        onSubmit={crear}
        className="rounded-xl border border-border bg-card p-4 mb-5 grid gap-3 sm:grid-cols-[1fr_160px_140px_140px_auto] sm:items-end"
      >
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Tarea</span>
          <input
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Vestir el carrito"
            required
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Punto</span>
          <select
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
          >
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Para</span>
          <input
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            value={asignado}
            onChange={(e) => setAsignado(e.target.value)}
            placeholder="cualquiera"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Día</span>
          <input
            type="date"
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            value={dia}
            onChange={(e) => setDia(e.target.value)}
          />
        </label>
        <button
          type="submit"
          disabled={!texto.trim() || !locationId}
          className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          Asignar
        </button>
      </form>

      {error && <ErrorBox error={error} />}
      {!items && !error && <Spinner />}
      {items && !items.length && (
        <Empty icon={ListTodo} title="Sin tareas" hint="Asigna una arriba; aparece en el teléfono de quien le toca." />
      )}

      <div className="grid gap-2">
        {items?.map((t) => (
          <div key={t.id} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between gap-3">
            <div>
              <p className={`text-foreground ${t.done_at ? 'line-through opacity-60' : ''}`}>
                {t.text}
              </p>
              <p className="text-xs text-muted-foreground">
                {nombre(t.location_id)} · {fecha(t.due_on)}
                {t.assigned_to ? ` · ${t.assigned_to}` : ' · sin asignar'}
                {t.done_by && ` · hecha por ${t.done_by}`}
              </p>
            </div>
            {t.done_at ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
            ) : (
              <Clock className="w-5 h-5 text-muted-foreground shrink-0" />
            )}
          </div>
        ))}
      </div>
    </Shell>
  );
}
